import { CalendarIcon, ClockIcon, RepeatIcon } from '@phosphor-icons/react';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { CronInput } from 'src/components/common/CronInput';
import type { ConfigRendererProps } from './types';

type PaymentScheduleMode = 'immediate' | 'payRun' | 'latestDate';

const modeLabels: Record<PaymentScheduleMode, string> = {
  immediate: 'Immediate',
  payRun: 'Pay Run Schedule',
  latestDate: 'Before Due Date',
};

const modeDescriptions: Record<PaymentScheduleMode, string> = {
  immediate: 'Schedule payment immediately when step runs',
  payRun: 'Schedule on recurring pay run dates (e.g., every Friday)',
  latestDate: 'Schedule to arrive before the due date with buffer',
};

function ModeIcon({ mode }: { mode: PaymentScheduleMode }) {
  switch (mode) {
    case 'immediate':
      return <ClockIcon className="w-4 h-4" />;
    case 'payRun':
      return <RepeatIcon className="w-4 h-4" />;
    case 'latestDate':
      return <CalendarIcon className="w-4 h-4" />;
  }
}

const commonTimezones = [
  { label: 'UTC', value: 'UTC' },
  { label: 'US Eastern', value: 'America/New_York' },
  { label: 'US Central', value: 'America/Chicago' },
  { label: 'US Mountain', value: 'America/Denver' },
  { label: 'US Pacific', value: 'America/Los_Angeles' },
];

/**
 * Config renderer for the schedule_payment step
 * Allows configuring payment scheduling mode and related options
 */
export function SchedulePaymentConfigRenderer({
  config,
  onConfigChange,
}: ConfigRendererProps) {
  const currentMode = (config.mode as PaymentScheduleMode) || 'immediate';

  const handleModeChange = (mode: PaymentScheduleMode) => {
    // When mode changes, reset to mode-specific defaults
    const baseConfig = { mode };

    if (mode === 'payRun') {
      onConfigChange({
        ...baseConfig,
        cron: (config.cron as string) || '0 9 * * 5',
        immediacy: (config.immediacy as string) || 'asap',
        bufferDays: (config.bufferDays as number) ?? 0,
      });
    } else if (mode === 'latestDate') {
      onConfigChange({
        ...baseConfig,
        bufferDays: (config.bufferDays as number) ?? 0,
      });
    } else {
      onConfigChange(baseConfig);
    }
  };

  const handleCronChange = (cron: string) => {
    onConfigChange({ ...config, cron });
  };

  const handleImmediacyChange = (immediacy: string) => {
    onConfigChange({ ...config, immediacy });
  };

  const handleRoundingChange = (rounding: string) => {
    if (rounding === 'none') {
      const { rounding: _, ...rest } = config;
      onConfigChange(rest);
    } else {
      onConfigChange({ ...config, rounding });
    }
  };

  const handleTimezoneChange = (timezone: string) => {
    if (timezone === 'UTC') {
      const { timezone: _, ...rest } = config;
      onConfigChange(rest);
    } else {
      onConfigChange({ ...config, timezone });
    }
  };

  const handleBufferDaysChange = (value: string) => {
    const bufferDays = parseInt(value, 10);
    if (!isNaN(bufferDays) && bufferDays >= 0) {
      onConfigChange({ ...config, bufferDays });
    }
  };

  return (
    <div className="space-y-4">
      {/* Mode Selection */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Scheduling Mode</Label>
        <RadioGroup
          value={currentMode}
          onValueChange={(value) =>
            handleModeChange(value as PaymentScheduleMode)
          }
          className="space-y-2"
        >
          {(['immediate', 'payRun', 'latestDate'] as const).map((mode) => (
            <div
              key={mode}
              className="flex items-start space-x-3 rounded-md border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
            >
              <RadioGroupItem
                value={mode}
                id={`mode-${mode}`}
                className="mt-1"
              />
              <div className="flex-1 space-y-1">
                <label
                  htmlFor={`mode-${mode}`}
                  className="flex items-center gap-2 text-sm font-medium cursor-pointer"
                >
                  <ModeIcon mode={mode} />
                  {modeLabels[mode]}
                </label>
                <p className="text-xs text-muted-foreground">
                  {modeDescriptions[mode]}
                </p>
              </div>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Mode-specific configuration */}
      {currentMode === 'payRun' && (
        <div className="space-y-4 pt-2 border-t">
          {/* Cron Expression with GUI */}
          <CronInput
            label="Pay Run Schedule"
            value={(config.cron as string) || '0 9 * * 5'}
            onChange={handleCronChange}
            defaultFrequency="weekly"
          />

          {/* Immediacy */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Scheduling Strategy</Label>
            <RadioGroup
              value={(config.immediacy as string) || 'asap'}
              onValueChange={handleImmediacyChange}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="asap" id="immediacy-asap" />
                <label
                  htmlFor="immediacy-asap"
                  className="text-sm cursor-pointer"
                >
                  Next pay run (ASAP)
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="latest" id="immediacy-latest" />
                <label
                  htmlFor="immediacy-latest"
                  className="text-sm cursor-pointer"
                >
                  Last pay run before due
                </label>
              </div>
            </RadioGroup>
            <p className="text-xs text-muted-foreground">
              {config.immediacy === 'latest'
                ? 'Payment will be scheduled on the last pay run that allows payment to arrive before the due date'
                : 'Payment will be scheduled on the next available pay run'}
            </p>
          </div>

          {/* Buffer Days */}
          <div className="space-y-2">
            <Label htmlFor="bufferDays" className="text-sm font-medium">
              Buffer Days Before Due Date
            </Label>
            <Input
              id="bufferDays"
              type="number"
              min={0}
              value={(config.bufferDays as number) ?? 0}
              onChange={(e) => handleBufferDaysChange(e.target.value)}
              className="w-24"
            />
            <p className="text-xs text-muted-foreground">
              Additional safety margin (in business days) to ensure payment
              arrives before the due date.
            </p>
          </div>

          {/* Rounding (optional) */}
          <div className="space-y-2">
            <Label htmlFor="rounding" className="text-sm font-medium">
              Rounding (Optional)
            </Label>
            <Select
              value={(config.rounding as string) || 'none'}
              onValueChange={handleRoundingChange}
            >
              <SelectTrigger id="rounding">
                <SelectValue placeholder="No rounding" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No rounding</SelectItem>
                <SelectItem value="up">Round up (later pay run)</SelectItem>
                <SelectItem value="down">
                  Round down (earlier pay run)
                </SelectItem>
                <SelectItem value="nearest">Round to nearest</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Timezone (optional) */}
          <div className="space-y-2">
            <Label htmlFor="timezone" className="text-sm font-medium">
              Timezone (Optional)
            </Label>
            <Select
              value={(config.timezone as string) || 'UTC'}
              onValueChange={handleTimezoneChange}
            >
              <SelectTrigger id="timezone">
                <SelectValue placeholder="UTC" />
              </SelectTrigger>
              <SelectContent>
                {commonTimezones.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {currentMode === 'latestDate' && (
        <div className="space-y-4 pt-2 border-t">
          <div className="space-y-2">
            <Label htmlFor="bufferDaysLatest" className="text-sm font-medium">
              Buffer Days Before Due Date
            </Label>
            <Input
              id="bufferDaysLatest"
              type="number"
              min={0}
              value={(config.bufferDays as number) ?? 0}
              onChange={(e) => handleBufferDaysChange(e.target.value)}
              className="w-24"
            />
            <p className="text-xs text-muted-foreground">
              Payment will be scheduled to arrive this many business days before
              the invoice due date. Payment processing time is automatically
              accounted for based on the supplier&apos;s payment method.
            </p>
          </div>
        </div>
      )}

      {currentMode === 'immediate' && (
        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground">
            Payment will be scheduled immediately when this step runs.
            Processing time depends on the supplier&apos;s configured payment
            method (e.g., ACH takes ~2 business days, FedNow is instant).
          </p>
        </div>
      )}
    </div>
  );
}
